import React, { useState, useEffect } from 'react';
import Topbar from '../../layouts/Topbar';
import Sidebar from '../../layouts/Sidebar';
import { toast } from 'react-toastify';
import axios from 'axios';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../../config/api';

const COSImprovementPlans = () => {
  const [improvementPlans, setImprovementPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const baseUrl = API_BASE_URL;
  const navigate = useNavigate();

  useEffect(() => {
    fetchImprovementPlans();
  }, []);

  const fetchImprovementPlans = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = Cookies.get('token');
      const response = await axios.get(`${baseUrl}/api/users/me/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const plans = Array.isArray(response.data.employee.improvement_plans)
        ? response.data.employee.improvement_plans
        : [];
      setImprovementPlans(plans);
    } catch (err) {
      console.error('Error fetching improvement plans:', err);
      setError('Failed to fetch improvement plans');
      toast.error('Failed to fetch improvement plans');
    } finally {
      setLoading(false);
    }
  };

  const openDetails = (plan) => {
    setSelectedPlan(plan);
    setDetailsOpen(true);
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setSelectedPlan(null);
  };

  const handleEditPlan = () => {
    navigate(`/cos/edit-improvement-plan/${selectedPlan.id}`);
  };

  const handleDeletePlan = () => setDeleteModalOpen(true);

  const confirmDelete = async () => {
    try {
      const token = Cookies.get('token');
      if (!selectedPlan?.id) throw new Error('No plan selected');
      await axios.delete(
        `${baseUrl}/api/improvement-plans/${selectedPlan.id}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      toast.success('Improvement plan deleted successfully');
      setDeleteModalOpen(false);
      setDetailsOpen(false);
      setSelectedPlan(null);
      fetchImprovementPlans();
    } catch (error) {
      console.error('Error deleting plan:', error);
      toast.error('Failed to delete improvement plan');
    }
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const splitToList = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(Boolean);
    return String(value)
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  };

  const renderApprovalBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return (
          <span className="badge bg-success-subtle text-success border border-success-subtle">
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="badge bg-danger-subtle text-danger border border-danger-subtle">
            Rejected
          </span>
        );
      default:
        return (
          <span className="badge bg-warning-subtle text-warning border border-warning-subtle">
            Pending
          </span>
        );
    }
  };

  return (
    <div
      data-sidebar-skin="dark"
      data-header-skin="light"
      data-navbar-brand-skin="dark"
      data-sidebar-state="expand"
    >
      <Topbar />
      <Sidebar />

      <div className="page-wrapper">
        <div className="content pb-0">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
            <div>
              <h4 className="fw-bold mb-1">Improvement Plans</h4>
              <p className="text-muted mb-0 fs-13">
                Browse your improvement plans
              </p>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-primary d-flex align-items-center gap-1"
              onClick={() => navigate('/cos/create-improvement-plan')}
            >
              <i className="ti ti-plus" /> Create Improvement Plan
            </button>
          </div>

          {loading ? (
            <div className="card shadow-sm p-5 text-center">
              <div
                className="spinner-border text-primary mx-auto mb-2"
                role="status"
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mb-0 fs-13">
                Loading improvement plans...
              </p>
            </div>
          ) : error ? (
            <div className="alert alert-danger shadow-sm" role="alert">
              <i className="ti ti-alert-circle me-2" />
              {error}
            </div>
          ) : improvementPlans.length === 0 ? (
            <div className="card shadow-sm p-5 text-center text-muted">
              <i className="ti ti-file-text-off fs-36 mb-2 text-secondary opacity-50" />
              <h5 className="fw-bold text-dark mb-1">
                No Improvement Plans Found
              </h5>
              <p className="fs-13 text-muted mb-0">
                You can create a new improvement plan for this year.
              </p>
            </div>
          ) : (
            <div className="row g-3">
              {improvementPlans.map((plan) => (
                <div key={plan.id} className="col-md-6 col-lg-4">
                  <div
                    className="card shadow-sm h-100 border"
                    role="button"
                    tabIndex={0}
                    onClick={() => openDetails(plan)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') openDetails(plan);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="card-body">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <h6 className="fw-bold text-dark mb-0">
                          Improvement Plan {plan.year || ''}
                        </h6>
                        {renderApprovalBadge(plan.approval_status)}
                      </div>

                      <div className="text-muted fs-13 mb-2">
                        <i className="ti ti-calendar me-1" />
                        Created: {formatDate(plan.date_created)}
                      </div>
                    </div>
                    <div className="card-footer bg-light py-2 px-3 d-flex align-items-center justify-content-between">
                      <span className="text-primary fs-13 fw-semibold">
                        View Details
                      </span>
                      <i className="ti ti-chevron-right text-primary" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
          <p className="text-dark mb-0">
            2026 © <span className="link-primary">Casoe Technologies</span>, All
            Rights Reserved
          </p>
        </div>
      </div>

      {detailsOpen && selectedPlan && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content shadow-lg border-0">
              <div className="modal-header">
                <div className="d-flex align-items-center gap-2">
                  <h5 className="modal-title fw-bold">
                    Improvement Plan {selectedPlan.year}
                  </h5>
                  {renderApprovalBadge(selectedPlan.approval_status)}
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeDetails}
                  aria-label="Close"
                />
              </div>
              <div className="modal-body">
                <div className="text-muted fs-13 mb-3">
                  <i className="ti ti-calendar me-1" />
                  Created Date: {formatDate(selectedPlan.date_created)}
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <div className="card bg-light border-0 h-100">
                      <div className="card-body">
                        <h6 className="fw-bold text-dark mb-2">Strengths</h6>
                        {splitToList(selectedPlan.strengths).length > 0 ? (
                          <ul className="mb-0 ps-3 fs-14">
                            {splitToList(selectedPlan.strengths).map(
                              (item, idx) => (
                                <li key={`strength-${idx}`}>{item}</li>
                              ),
                            )}
                          </ul>
                        ) : (
                          <p className="text-muted fs-13 mb-0">
                            No strengths specified
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="card bg-light border-0 h-100">
                      <div className="card-body">
                        <h6 className="fw-bold text-dark mb-2">
                          Areas for Improvement
                        </h6>
                        {splitToList(selectedPlan.weaknesses).length > 0 ? (
                          <ul className="mb-0 ps-3 fs-14">
                            {splitToList(selectedPlan.weaknesses).map(
                              (item, idx) => (
                                <li key={`weakness-${idx}`}>{item}</li>
                              ),
                            )}
                          </ul>
                        ) : (
                          <p className="text-muted fs-13 mb-0">
                            No weaknesses specified
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="card bg-light border-0 h-100">
                      <div className="card-body">
                        <h6 className="fw-bold text-dark mb-2">
                          Training Needs
                        </h6>
                        {splitToList(selectedPlan.training_needs).length > 0 ? (
                          <ul className="mb-0 ps-3 fs-14">
                            {splitToList(selectedPlan.training_needs).map(
                              (item, idx) => (
                                <li key={`training-${idx}`}>{item}</li>
                              ),
                            )}
                          </ul>
                        ) : (
                          <p className="text-muted fs-13 mb-0">
                            No training needs specified
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="card bg-light border-0 h-100">
                      <div className="card-body">
                        <h6 className="fw-bold text-dark mb-2">
                          Employee Comment
                        </h6>
                        <p
                          className="text-dark fs-14 mb-0"
                          style={{ whiteSpace: 'pre-wrap' }}
                        >
                          {selectedPlan.employee_comment ||
                            'No comment provided'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {selectedPlan.approval_status === 'approved' && (
                  <div className="card bg-success-subtle border border-success-subtle mb-3">
                    <div className="card-body">
                      <h6 className="fw-bold text-success mb-2">
                        Supervisor Feedback
                      </h6>
                      <p
                        className="text-dark fs-14 mb-0"
                        style={{ whiteSpace: 'pre-wrap' }}
                      >
                        {selectedPlan.supervisor_comment ||
                          'No supervisor comment provided'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                {(selectedPlan.approval_status === 'pending' ||
                  selectedPlan.approval_status === 'rejected') && (
                  <>
                    <button
                      type="button"
                      onClick={handleEditPlan}
                      className="btn btn-warning text-white"
                    >
                      <i className="ti ti-edit me-1" /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={handleDeletePlan}
                      className="btn btn-danger"
                    >
                      <i className="ti ti-trash me-1" /> Delete
                    </button>
                  </>
                )}
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeDetails}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteModalOpen && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1060 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg border-0">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">
                  Delete Improvement Plan
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeDeleteModal}
                  aria-label="Close"
                />
              </div>
              <div className="modal-body">
                <p className="mb-2">
                  Are you sure you want to delete this improvement plan for{' '}
                  {selectedPlan?.year}?
                </p>
                <div className="text-danger fw-semibold fs-13">
                  This action cannot be undone.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  onClick={closeDeleteModal}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDelete}
                  className="btn btn-danger"
                >
                  Delete Plan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default COSImprovementPlans;
